import { z } from 'zod';

/**
 * This type allows TS to check the all keys of ProductConfigInputOptions exists in the array exactly once.
 * Ensures all the keys from the union exists (credit: https://catchts.com/union-array)
 */
export type TupleUnion<U extends string, R extends any[] = []> = {
  [S in U]: Exclude<U, S> extends never ? [...R, S] : TupleUnion<Exclude<U, S>, [...R, S]>;
}[U];

export function checkObjectKeysAreSorted<T extends object>(data: T, ctx: z.RefinementCtx) {
  let prevKey: string | undefined;
  for (const key of Object.keys(data)) {
    // Compare keys alphabetically. Hard-coded to English locale to avoid localization errors
    // See https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/localeCompare#locales
    if (prevKey && prevKey.localeCompare(key, 'en', { sensitivity: 'base' }) > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `The keys are not sorted. "${prevKey}" should come after "${key}".`,
      });
    }
    prevKey = key;
  }
}

export function checkMapKeyMatches<Config, K extends keyof Config>(data: Record<string, Config>, configKey: K, ctx: z.RefinementCtx) {
  for (const [key, config] of Object.entries(data)) {
    if (key === config[configKey]) {
      continue;
    }
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `The key "${key}" does not match the key "${config[configKey]}" in the configuration.`,
    });
  }
}
